import axios from "axios";
// Allow overriding the API base URL via environment variable during development
// (CRA-style `REACT_APP_` prefix). Fall back to the production host so the
// app keeps working even if a .env isn't configured yet.
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const STORAGE_KEYS = {
  accessToken: "vm_access_token",
  refreshToken: "vm_refresh_token",
  user: "vm_user",
  orgId: "vm_org_id",
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

// ── Request interceptor: attach Bearer token ──
api.interceptors.request.use(
  (config) => {
    const token = getStoredAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ── Response interceptor: silent refresh on 401 ──
let refreshPromise = null;
let permissionsChangedHandler = null;

export function registerPermissionsChangedHandler(handler) {
  permissionsChangedHandler = typeof handler === "function" ? handler : null;
}

export function extractApiError(err) {
  const resp = err?.response?.data || {};
  const envelope = resp?.error ? resp.error : resp;
  const message = envelope?.message || err?.message || "An error occurred.";
  const code = envelope?.code || null;
  const details = envelope?.details || null;
  const request_id = resp?.request_id || null;
  return { message, code, details, request_id, raw: resp };
}

export function unwrapSingle(res) {
  return res?.data?.data ?? res?.data ?? null;
}

function normalizeAxiosError(error) {
  const apiError = extractApiError(error);
  const normalizedError = new Error(apiError.message);
  normalizedError.code = apiError.code;
  normalizedError.details = apiError.details;
  normalizedError.request_id = apiError.request_id;
  normalizedError.response = error.response;
  normalizedError.raw = apiError.raw;
  return normalizedError;
}

/**
 * Performs a token refresh against POST /auth/refresh.
 * Deduplicates concurrent calls: if a refresh is already in-flight, returns
 * the same Promise so only one HTTP request ever hits the backend at a time.
 * Concurrent callers — the proactive timer in auth.js AND the reactive 401
 * interceptor — share the same result and can never race with separate tokens.
 *
 * Does NOT touch React state. Callers own clearSession() / redirect on error.
 * Throws on any failure (network, 4xx, missing access_token in response).
 */
export async function performTokenRefresh() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const currentRefreshToken = getStoredRefreshToken();
    if (!currentRefreshToken) throw new Error("No refresh token available");

    const { data: raw } = await axios.post(`${API_BASE_URL}/auth/refresh`, {
      refresh_token: currentRefreshToken,
    });

    const data = raw?.data || raw || {};
    const newAccess = data.access_token || data.accessToken || null;
    const newRefresh = data.refresh_token || data.refreshToken || null;
    const expiresIn = data.expires_in || data.expiresIn || 600;

    if (!newAccess) throw new Error("Refresh did not return access token");

    localStorage.setItem(STORAGE_KEYS.accessToken, newAccess);
    if (newRefresh) localStorage.setItem(STORAGE_KEYS.refreshToken, newRefresh);
    api.defaults.headers.common.Authorization = `Bearer ${newAccess}`;

    return { accessToken: newAccess, refreshToken: newRefresh || currentRefreshToken, expiresIn };
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const apiError = extractApiError(error);
    const isPermissionsChanged = apiError?.details?.reason === "permissions_changed";

    // Only attempt refresh on 401, not for auth endpoints themselves
    if (
      error.response?.status !== 401 ||
      originalRequest._retry ||
      originalRequest.url?.includes("/auth/login") ||
      originalRequest.url?.includes("/auth/refresh")
    ) {
      return Promise.reject(normalizeAxiosError(error));
    }

    originalRequest._retry = true;

    try {
      const { accessToken: newAccess } = await performTokenRefresh();
      originalRequest.headers.Authorization = `Bearer ${newAccess}`;

      if (isPermissionsChanged && permissionsChangedHandler) {
        Promise.resolve()
          .then(() => permissionsChangedHandler())
          .catch((err) => console.warn("[api] permissions_changed re-sync failed:", err));
      }

      return api(originalRequest);
    } catch (refreshError) {
      clearSession();
      window.location.href = "/login";
      return Promise.reject(normalizeAxiosError(refreshError));
    }
  },
);

// ── Storage helpers ──
export function storeSession(accessToken, refreshToken, user) {
  localStorage.setItem(STORAGE_KEYS.accessToken, accessToken);
  if (refreshToken) {
    localStorage.setItem(STORAGE_KEYS.refreshToken, refreshToken);
  } else {
    localStorage.removeItem(STORAGE_KEYS.refreshToken);
  }
  localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(user));
  if (user?.org_id) {
    localStorage.setItem(STORAGE_KEYS.orgId, user.org_id);
  } else {
    localStorage.removeItem(STORAGE_KEYS.orgId);
  }
  if (accessToken) {
    api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  }
}

export function getStoredUser() {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.user);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getStoredOrgId() {
  const orgId = localStorage.getItem(STORAGE_KEYS.orgId);
  if (orgId) return orgId;

  const user = getStoredUser();
  return user?.org_id || null;
}

export function getStoredAccessToken() {
  return localStorage.getItem(STORAGE_KEYS.accessToken);
}

export function getStoredRefreshToken() {
  const token = localStorage.getItem(STORAGE_KEYS.refreshToken);
  return token && token !== "null" && token !== "undefined" ? token : null;
}

export function clearSession() {
  localStorage.removeItem(STORAGE_KEYS.accessToken);
  localStorage.removeItem(STORAGE_KEYS.refreshToken);
  localStorage.removeItem(STORAGE_KEYS.user);
  localStorage.removeItem(STORAGE_KEYS.orgId);
  // Also clear old session key if it exists
  localStorage.removeItem("iiot_safety_session");
}

export { STORAGE_KEYS };
export default api;
